
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { swapsAPI } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import './Swaps.css';

const STATUS_COLORS = {
  pending: 'warning',
  accepted: 'success',
  rejected: 'danger',
  completed: 'gray'
};

const SwapCard = ({ swap, currentUserId, onAccept, onReject, onComplete, onDelete, onMessage }) => {
  const isSender = swap.sender?._id === currentUserId;
  const other = isSender ? swap.receiver : swap.sender;
  const initials = other?.name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);

  const [pendingAction, setPendingAction] = useState(null);

  const handle = (action, fn) => async () => {
    setPendingAction(action);
    await fn();
    setPendingAction(null);
  };

  return (
    <div className={`swap-card ${swap.status}`}>
      <div className="swap-card-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div className="avatar" style={{ width: 42, height: 42, fontSize: 15 }}>
            {other?.avatar
              ? <img src={other.avatar} alt={other.name} style={{ width: '100%', height: '100%', borderRadius: '50%' }} />
              : initials}
          </div>
          <div>
            <p className="swap-other-name">{other?.name || 'Unknown'}</p>
            <p className="swap-direction">{isSender ? 'You requested' : 'Requested from you'}</p>
          </div>
        </div>
        <span className={`badge badge-${STATUS_COLORS[swap.status]}`}>
          {swap.status.charAt(0).toUpperCase() + swap.status.slice(1)}
        </span>
      </div>

      <div className="swap-skills">
        <div className="swap-skill-box">
          <span className="swap-skill-label">Teaching</span>
          <span className="tag">{swap.skillOffered}</span>
        </div>
        <span className="swap-arrow">⇄</span>
        <div className="swap-skill-box">
          <span className="swap-skill-label">Learning</span>
          <span className="tag tag-wanted">{swap.skillWanted}</span>
        </div>
      </div>

      {swap.message && (
        <p className="swap-message">"{swap.message}"</p>
      )}

      {swap.scheduledTime && (
        <p className="swap-time">
          🗓 {new Date(swap.scheduledTime).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
        </p>
      )}

      {swap.status === 'accepted' && (
        <div className="swap-chat-banner">
          <span>🎉 Swap accepted! You can now chat with {other?.name?.split(' ')[0]}.</span>
          <button
            className="btn btn-primary btn-sm"
            onClick={() => onMessage(other)}
          >
            Open Chat →
          </button>
        </div>
      )}

      <div className="swap-card-footer">
        <span className="swap-date">{new Date(swap.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
        <div className="swap-actions">
          {swap.status === 'pending' && !isSender && (
            <>
              {pendingAction !== 'reject' && (
                <button
                  className="btn btn-success btn-sm"
                  disabled={!!pendingAction}
                  onClick={handle('accept', () => onAccept(swap._id))}
                >
                  {pendingAction === 'accept'
                    ? <><span className="loading-spinner" style={{ borderTopColor: 'white', width: 12, height: 12 }} /> Accepting…</>
                    : 'Accept'}
                </button>
              )}
              {pendingAction !== 'accept' && (
                <button
                  className="btn btn-danger btn-sm"
                  disabled={!!pendingAction}
                  onClick={handle('reject', () => onReject(swap._id))}
                >
                  {pendingAction === 'reject'
                    ? <><span className="loading-spinner" style={{ borderTopColor: 'white', width: 12, height: 12 }} /> Rejecting…</>
                    : 'Reject'}
                </button>
              )}
            </>
          )}
          {swap.status === 'pending' && isSender && (
            <button
              className="btn btn-ghost btn-sm"
              disabled={pendingAction === 'delete'}
              onClick={handle('delete', () => onDelete(swap._id))}
            >
              {pendingAction === 'delete'
                ? <><span className="loading-spinner loading-spinner-dark" style={{ width: 12, height: 12 }} /> Cancelling…</>
                : 'Cancel'}
            </button>
          )}
          {swap.status === 'accepted' && (
            <button
              className="btn btn-outline btn-sm"
              disabled={pendingAction === 'complete'}
              onClick={handle('complete', () => onComplete(swap._id))}
            >
              {pendingAction === 'complete'
                ? <><span className="loading-spinner loading-spinner-dark" style={{ width: 12, height: 12 }} /> Saving…</>
                : 'Mark Complete'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const Swaps = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [swaps, setSwaps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  const fetchSwaps = async () => {
    setLoading(true);
    try {
      const { data } = await swapsAPI.getAll();
      setSwaps(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchSwaps(); }, []);

  const handleAccept = async (id) => {
    setSwaps(s => s.map(x => x._id === id ? { ...x, status: 'accepted' } : x));
    try {
      const { data } = await swapsAPI.accept(id);
      setSwaps(s => s.map(x => x._id === id ? data : x));
    } catch {
      fetchSwaps();
    }
  };

  const handleReject = async (id) => {
    setSwaps(s => s.map(x => x._id === id ? { ...x, status: 'rejected' } : x));
    try {
      const { data } = await swapsAPI.reject(id);
      setSwaps(s => s.map(x => x._id === id ? data : x));
    } catch {
      fetchSwaps();
    }
  };

  const handleComplete = async (id) => {
    setSwaps(s => s.map(x => x._id === id ? { ...x, status: 'completed' } : x));
    try {
      const { data } = await swapsAPI.complete(id);
      setSwaps(s => s.map(x => x._id === id ? data : x));
    } catch {
      fetchSwaps();
    }
  };

  const handleDelete = async (id) => {
    setSwaps(s => s.filter(x => x._id !== id));
    try {
      await swapsAPI.delete(id);
    } catch {
      fetchSwaps();
    }
  };

  const handleMessage = (otherUser) => {
    navigate(`/messages?userId=${otherUser._id}&name=${encodeURIComponent(otherUser.name)}`);
  };

  const FILTERS = ['all', 'pending', 'accepted', 'completed', 'rejected'];
  const filtered = filter === 'all' ? swaps : swaps.filter(s => s.status === filter);

  const counts = FILTERS.reduce((acc, f) => {
    acc[f] = f === 'all' ? swaps.length : swaps.filter(s => s.status === f).length;
    return acc;
  }, {});

  return (
    <div className="swaps-page">
      <div className="page-container">
        <div className="swaps-header">
          <h1>My Swaps</h1>
          <p>Manage your skill exchange requests</p>
        </div>

        <div className="swaps-filters">
          {FILTERS.map(f => (
            <button
              key={f}
              className={`filter-chip ${filter === f ? 'active' : ''}`}
              onClick={() => setFilter(f)}
            >
              {f.charAt(0).toUpperCase() + f.slice(1)}
              {counts[f] > 0 && <span className="filter-count">{counts[f]}</span>}
            </button>
          ))}
        </div>

        {loading ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {[...Array(3)].map((_, i) => <div key={i} className="card-skeleton" style={{ height: 180 }} />)}
          </div>
        ) : filtered.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">⇄</div>
            <h3>No {filter !== 'all' ? filter : ''} swaps yet</h3>
            <p>Go to the home page to find people to swap skills with</p>
          </div>
        ) : (
          <div className="swaps-grid">
            {filtered.map(swap => (
              <SwapCard
                key={swap._id}
                swap={swap}
                currentUserId={user?._id}
                onAccept={handleAccept}
                onReject={handleReject}
                onComplete={handleComplete}
                onDelete={handleDelete}
                onMessage={handleMessage}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Swaps;